import type { AffiliateCampaignTerms, InfluencerCampaign } from "@/marketing/models/influencerCampaign";

// Raw input strings; a blank field means the programme default applies.
export interface AffiliateTermsFormState {
  discountPercent: string;
  couponPercent: string;
  utmPercent: string;
  couponUtmPercent: string;
}

const RATE_FIELDS = ["couponPercent", "utmPercent", "couponUtmPercent"] as const;

const RATE_LABELS: Record<(typeof RATE_FIELDS)[number], string> = {
  couponPercent: "Code commission",
  utmPercent: "Link commission",
  couponUtmPercent: "Code + link commission",
};

export function emptyAffiliateTermsForm(): AffiliateTermsFormState {
  return { discountPercent: "", couponPercent: "", utmPercent: "", couponUtmPercent: "" };
}

export function readAffiliateTermsForm(campaign: InfluencerCampaign | undefined): AffiliateTermsFormState {
  const terms = campaign?.affiliateTerms;
  return {
    discountPercent: terms?.discountPercent != null ? String(terms.discountPercent) : "",
    couponPercent: terms?.couponPercent != null ? String(terms.couponPercent) : "",
    utmPercent: terms?.utmPercent != null ? String(terms.utmPercent) : "",
    couponUtmPercent: terms?.couponUtmPercent != null ? String(terms.couponUtmPercent) : "",
  };
}

// Undefined when every field is blank, so the campaign keeps following the programme defaults.
export function buildAffiliateTerms(form: AffiliateTermsFormState): AffiliateCampaignTerms | undefined {
  const terms: AffiliateCampaignTerms = {};
  const discount = form.discountPercent.trim();
  if (discount) terms.discountPercent = Number(discount);
  for (const field of RATE_FIELDS) {
    const rate = form[field].trim();
    if (rate) terms[field] = Number(rate);
  }
  return Object.keys(terms).length > 0 ? terms : undefined;
}

// Mirrors the Facade's validation: a whole-percent discount from 1 to 100, and rates from 0 to 100.
export function affiliateTermsErrors(form: AffiliateTermsFormState): string[] {
  const errors: string[] = [];
  const discount = form.discountPercent.trim();
  if (discount) {
    const discountPercent = Number(discount);
    if (!Number.isInteger(discountPercent) || discountPercent < 1 || discountPercent > 100) {
      errors.push("Customer discount must be a whole percent from 1 to 100.");
    }
  }
  for (const field of RATE_FIELDS) {
    const rate = form[field].trim();
    if (!rate) continue;
    const ratePercent = Number(rate);
    if (!Number.isFinite(ratePercent) || ratePercent < 0 || ratePercent > 100) {
      errors.push(`${RATE_LABELS[field]} must be a percent from 0 to 100.`);
    }
  }
  return errors;
}
